/**
 * Pure helpers for the shared artwork tag dictionary
 * (`DOC_TYPES.ARTWORK_TAGS`). No React, no Firestore — `useArtworkTags` wraps
 * these, and they are unit-tested in `artworkTagHelpers.test.ts`.
 *
 * Projects store tag IDS, never tag text: an id is what makes a future central
 * rename possible. Everything that renders text therefore has to resolve
 * through the dictionary, and every resolver here tolerates an id the
 * dictionary no longer has (a retired or deleted tag) by omitting it rather
 * than rendering a raw id.
 */
import { sortBy } from "lodash"

import { type ArtworkTagConstructor } from "#customTypes/composedTypes/artworkTagTypes"
import { type Uuid } from "#customTypes/globalTypes"

/** The shape the picker renders: dictionary entry plus its usage count. */
export interface ArtworkTagOption {
  id: Uuid
  text: string
  usageCount: number
}

/** Anything carrying tag ids — a stored project or its UI model. */
export interface ArtworkTagged {
  tagIds?: readonly Uuid[]
}

/** Trim, then collapse internal whitespace runs to single spaces. */
export const normalizeArtworkTagText = (text: string): string =>
  text.trim().replace(/\s+/g, " ")

/** Case-insensitive text lookup, so a re-typed tag never becomes a duplicate. */
export const findArtworkTagByText = (
  tags: readonly ArtworkTagConstructor[],
  text: string
): ArtworkTagConstructor | undefined => {
  const key = normalizeArtworkTagText(text).toLowerCase()
  if (!key) return undefined
  return tags.find(
    (tag) => normalizeArtworkTagText(tag.text).toLowerCase() === key
  )
}

/** Drops empties and duplicates, keeping first-seen order. */
export const normalizeArtworkTagIds = (tagIds: readonly Uuid[]): Uuid[] =>
  Array.from(new Set(tagIds.filter((id) => !!id?.trim())))

/** How many of `projects` carry each tag id. Unused tags are absent. */
export const countArtworkTagUsage = (
  projects: readonly ArtworkTagged[]
): Record<Uuid, number> => {
  const counts: Record<Uuid, number> = {}
  projects.forEach((project) => {
    normalizeArtworkTagIds(project.tagIds ?? []).forEach((tagId) => {
      counts[tagId] = (counts[tagId] ?? 0) + 1
    })
  })
  return counts
}

/** Most-used first, then alphabetical (case-insensitive) so ties are stable. */
export const orderArtworkTagsForPicker = (
  tags: readonly ArtworkTagConstructor[],
  usageCounts: Record<Uuid, number>
): ArtworkTagOption[] =>
  sortBy(
    tags.map((tag) => ({
      id: tag.id,
      text: tag.text,
      usageCount: usageCounts[tag.id] ?? 0,
    })),
    (option) => -option.usageCount,
    (option) => option.text.toLowerCase()
  )

/**
 * Tag text for a list of ids, in the order given. An id the dictionary cannot
 * resolve is skipped — a retired tag must never crash a tile or a timeline
 * entry, and a raw id on screen is worse than a missing chip.
 */
export const resolveArtworkTagTexts = (
  tagIds: readonly Uuid[],
  tagsById: Record<Uuid, ArtworkTagConstructor | undefined>
): string[] =>
  tagIds
    .map((tagId) => tagsById[tagId]?.text)
    .filter((text): text is string => !!text?.trim())
